import React, { useEffect, useMemo, useState } from "react";
import styles from "./BonusTab.module.css";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getAllRanking,
  getAllRankingHistoryByID,
  getUsersByID,
  getPlayerBalance,
} from "../../store/slices/user";
import apiClient from "../../store/services/apiClient";

export default function BonusTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const {
    allRanking = [],
    allRankingHistoryByID = [],
    singleUsers = [],
    colorSettings = [],
  } = useSelector((s) => s.user || {});

  const [openReceive, setOpenReceive] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [message, setMessage] = useState("");

  const apiBase = process.env.REACT_APP_API_BASE_URL || "";
  const primaryColor = colorSettings?.[0]?.primaryColor || "#f9b234";
  const mainText = colorSettings?.[0]?.mainText || "#111";

  useEffect(() => {
    dispatch(getAllRanking());
    if (user?.id) {
      dispatch(getAllRankingHistoryByID(user.id));
    }
  }, [dispatch, user?.id]);

  const currentUser = singleUsers?.[0] || {};
  const currentClass = Number(currentUser?.rankClass ?? 0);
  const currentRank = allRanking?.[currentClass] || {};
  const nextRank = allRanking?.[currentClass + 1] || null;
  const depositAmount = Number(currentUser?.deposit_notpro_amount ?? 0);
  const target = Number(nextRank?.rankReceiveConditionAmount ?? 0);
  const progress =
    target > 0
      ? Math.min(100, Math.round((depositAmount / target) * 100))
      : 100;

  const filteredRankingHistory = useMemo(
    () =>
      (allRankingHistoryByID || []).filter(
        (i) => i?.receiveStatus === "RECEIVING",
      ),
    [allRankingHistoryByID],
  );

  const canCheckBonus =
    currentUser?.PromotionStatus === "INACTIVE" &&
    (currentUser?.withdrawConditionType === null ||
      currentUser?.withdrawConditionType === undefined);

  const handleCheckBonus = async () => {
    if (!user?.id) return;
    setMessage("");
    try {
      await apiClient.get(`/ranking/checkrank/${user.id}`);
      await dispatch(getAllRankingHistoryByID(user.id));
    } catch (e) {
      // ignore
    }
    setOpenReceive(true);
  };

  const handleReceiveBonus = async (rowId) => {
    if (!user?.id) return;
    setLoadingId(rowId);
    setMessage("");
    try {
      const body = { data: rowId };
      const r = await apiClient.post(`/ranking/receiverank/${user.id}`, body);
      const msg = r?.data?.message || "";
      const ok =
        msg === "รับโบนัสเลื่อนแรงค์ with Withdrawal Condition" ||
        msg === "รับโบนัสเลื่อนแรงค์ Successfully";
      if (ok) {
        setMessage("รับโบนัสเลื่อนระดับเรียบร้อยแล้ว");
        setTimeout(async () => {
          await dispatch(getUsersByID(user));
          await dispatch(getPlayerBalance(user));
          setLoadingId(null);
          setOpenReceive(false);
        }, 1200);
      } else {
        setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเลื่อนระดับ");
        setTimeout(() => setLoadingId(null), 800);
      }
    } catch (e) {
      setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเลื่อนระดับ");
      setTimeout(() => setLoadingId(null), 800);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h4 className={styles.title}>🏆 โปรแกรมโบนัสสมาชิก</h4>
        <p className={styles.subtitle}>ระบบผลตอบแทนตามระดับสมาชิก</p>
      </div>

      <div className={styles.panel}>
        <div className={styles.rankCard}>
          <div className={styles.rankHeader}>
            {currentRank?.rankImageURL ? (
              <img
                className={styles.rankBadge}
                src={`${apiBase}/uploads${currentRank.rankImageURL}`}
                alt={currentRank?.rankName || "rank"}
                loading="lazy"
              />
            ) : (
              <div className={styles.rankBadgePlaceholder}>🏅</div>
            )}
            <div className={styles.rankInfo}>
              <div className={styles.rankName}>
                ระดับปัจจุบัน: {currentRank?.rankName || "-"}
              </div>
              {nextRank ? (
                <div className={styles.rankNote}>
                  ต้องมียอดฝากครบ {nextRank?.rankReceiveConditionAmount ?? 0}{" "}
                  บาท เพื่อเลื่อนระดับถัดไป
                </div>
              ) : (
                <div className={styles.rankNote}>ท่านอยู่ระดับสูงสุดแล้ว</div>
              )}
            </div>
          </div>
          {nextRank && (
            <div className={styles.progressWrap}>
              <div className={styles.progressTop}>
                <span className={styles.progressTitle}>ความคืบหน้า</span>
                <span className={styles.progressLabel}>
                  {depositAmount.toLocaleString()}/{target.toLocaleString()} บาท
                </span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className={styles.progressPercent}>{progress}%</div>
            </div>
          )}
          <div className={styles.actionRow}>
            {canCheckBonus ? (
              <button className={styles.primaryBtn} onClick={handleCheckBonus}>
                ตรวจสอบโบนัสผลตอบแทน
              </button>
            ) : (
              <div
                className={styles.warningNote}
                style={{ textAlign: "center" }}
              >
                <div className={styles.warningIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9V13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <h4>ไม่สามารถรับโบนัสได้ในขณะนี้</h4>
                  <p>
                    ท่านอยู่ระหว่างใช้โปรโมชั่น หรือมียอดที่ต้องเล่น
                    กรุณาเคลียร์ยอดเล่นให้ครบก่อนรับโบนัสเลื่อนระดับ
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.grid}>
        {allRanking?.map((row, index) => {
          const isCurrent = currentClass === index;
          const isDone = currentClass > index;
          return (
            <div
              key={index}
              className={[
                styles.card,
                isCurrent ? styles.cardCurrent : "",
                isDone ? styles.cardDone : "",
              ].join(" ")}
              style={{ borderColor: isCurrent ? primaryColor : "transparent" }}
            >
              <div className={styles.cardTop}>
                {row?.rankImageURL ? (
                  <img
                    className={styles.cardImage}
                    src={`${apiBase}/uploads${row.rankImageURL}`}
                    alt={row?.rankName || "rank"}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.cardImagePlaceholder}>🎖️</div>
                )}
                {isCurrent && <div className={styles.badgeNow}>ปัจจุบัน</div>}
                {isDone && <div className={styles.badgeDone}>สำเร็จ</div>}
              </div>
              <div className={styles.cardTitle}>{row?.rankName || "-"}</div>
              <div className={styles.cardSub}>
                [ยอด {row?.rankReceiveConditionAmount ?? 0} บาท]
              </div>
              <div className={styles.reward}>
                วงเงินกู้ {row?.rankBonusAmount ?? 0} เครดิต
              </div>
            </div>
          );
        })}
      </div>

      {openReceive && (
        <div
          className={styles.modalOverlay}
          onClick={() => setOpenReceive(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>รับโบนัสระดับสมาชิก</div>
              <button
                className={styles.closeBtn}
                onClick={() => setOpenReceive(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {message && <div className={styles.inlineMessage}>{message}</div>}

              {filteredRankingHistory.length === 0 && (
                <div className={styles.emptyBox}>ยังไม่มีโบนัสระดับสมาชิก</div>
              )}

              {filteredRankingHistory.map((row, index) => {
                const before = allRanking?.[Number(row?.rankclassBefore) || 0];
                const after = allRanking?.[Number(row?.rankclassAfter) || 0];
                return (
                  <div key={index} className={styles.historyCard}>
                    <div className={styles.historyRanks}>
                      {before?.rankImageURL ? (
                        <img
                          className={styles.historyBadge}
                          src={`${apiBase}/uploads${before.rankImageURL}`}
                          alt={before?.rankName || "before"}
                        />
                      ) : (
                        <span className={styles.historyBadge}>🏅</span>
                      )}
                      <span className={styles.arrow}>→</span>
                      {after?.rankImageURL ? (
                        <img
                          className={styles.historyBadge}
                          src={`${apiBase}/uploads${after.rankImageURL}`}
                          alt={after?.rankName || "after"}
                        />
                      ) : (
                        <span className={styles.historyBadge}>🏆</span>
                      )}
                    </div>
                    <div className={styles.historyText}>
                      {row?.withdrawCondition || ""}
                    </div>
                    {loadingId === row?.id && (
                      <div className={styles.loadingBar} />
                    )}
                    <button
                      className={styles.primaryBtn}
                      disabled={!!loadingId}
                      onClick={() => handleReceiveBonus(row?.id)}
                    >
                      รับโบนัสเลื่อนระดับ
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
